import { Alert, Link } from "@mui/material";

import { Product } from "../../../features/products/product";

/**
 * Warns under the Stock Quantity field when it disagrees with the variant total.
 *
 * A product is in stock when the product-level "Stock Quantity" OR any variant is above 0.
 * Shopify imports write both, so typing 0 here leaves the product in stock on a variant the
 * user never sees on this tab (Forever Catholic #325317, 2026-09-22: 36 products zeroed
 * here, all still in her catalog on a hidden variant at 100).
 *
 * Silent when there are no variants, the field is empty, or the two numbers agree.
 * Inventory is an in-app route, not a new tab: a second tab holds its own product db, so
 * edits made there never reach this one.
 */
export const StockFieldNotice = ({
  product,
  value,
  onShowVariants,
  onOpenInventory,
}: {
  product: Product;
  value: string | undefined;
  onShowVariants: () => void;
  onOpenInventory: () => void;
}) => {
  const variants = product.variants ?? [];
  if (variants.length === 0) return null;

  const variantTotal = variants.reduce((sum, v) => sum + (v.quantity || 0), 0);
  const typed = parseInt(value || "", 10);
  if (Number.isNaN(typed) || typed === variantTotal) return null;

  // Mirrors Item.SetStock: product-level OR any variant.
  const inStock = typed > 0 || variants.some((v) => (v.quantity || 0) > 0);

  return (
    <Alert severity="warning" sx={{ mt: 1, mb: 2, py: 0 }}>
      <b>{inStock ? "In Stock" : "Out of Stock"}</b> - variants hold{" "}
      {variantTotal}. Update them in{" "}
      <Link component="button" type="button" onClick={onShowVariants} sx={{ verticalAlign: "baseline" }}>
        Product Variants
      </Link>{" "}
      or{" "}
      <Link component="button" type="button" onClick={onOpenInventory} sx={{ verticalAlign: "baseline" }}>
        Inventory
      </Link>
      .
    </Alert>
  );
};
